import { computed, onScopeDispose, ref } from 'vue';

/**
 * Cabinet layout by viewport width (docs/cabinet-redesign/03_ДИЗАЙН_СИСТЕМА.md §5): a phone gets the bottom tab bar,
 * a tablet the 92 px navigation rail, a desktop the 216 px sidebar. One source for every component that needs to know —
 * the navigation is mounted with v-if from here, never hidden by a media query (hidden copies would duplicate tab-* test ids).
 */
export type LayoutMode = 'phone' | 'rail' | 'desktop';

const RAIL_QUERY = '(min-width: 700px)';
const DESKTOP_QUERY = '(min-width: 1100px)';

function read(): LayoutMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'phone';
  if (window.matchMedia(DESKTOP_QUERY).matches) return 'desktop';
  if (window.matchMedia(RAIL_QUERY).matches) return 'rail';
  return 'phone';
}

const mode = ref<LayoutMode>(read());
let listeners = 0;
let detach: (() => void) | null = null;

function attach() {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  const queries = [window.matchMedia(RAIL_QUERY), window.matchMedia(DESKTOP_QUERY)];
  const update = () => { mode.value = read(); };
  queries.forEach(q => q.addEventListener('change', update));
  detach = () => queries.forEach(q => q.removeEventListener('change', update));
}

export function useLayout() {
  if (listeners++ === 0) { mode.value = read(); attach(); }
  onScopeDispose(() => {
    if (--listeners === 0 && detach) { detach(); detach = null; }
  });
  return {
    mode,
    isPhone: computed(() => mode.value === 'phone'),
    isRail: computed(() => mode.value === 'rail'),
    isDesktop: computed(() => mode.value === 'desktop'),
  };
}
